import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Volume2, Play, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const VOICE_COLORS = {
  alloy: 'border-blue-500/30 bg-blue-500/5 text-blue-400',
  echo: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-400',
  fable: 'border-amber-500/30 bg-amber-500/5 text-amber-400',
  onyx: 'border-gray-500/30 bg-gray-500/5 text-gray-300',
  nova: 'border-pink-500/30 bg-pink-500/5 text-pink-400',
  shimmer: 'border-purple-500/30 bg-purple-500/5 text-purple-400',
};

function VoiceCard({ voice, selected, onSelect, onPreview, previewing }) {
  const isActive = selected === voice.id;
  return (
    <div
      className={`relative w-full text-left px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
        isActive
          ? `${VOICE_COLORS[voice.id]} ring-1 ring-white/10`
          : 'border-white/5 bg-white/[0.02] text-gray-400 hover:border-white/10'
      }`}
      onClick={() => onSelect(voice.id)}
      data-testid={`voice-option-${voice.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <span className="text-xs font-medium">{voice.name}</span>
          <p className="text-[9px] text-gray-500 mt-0.5">{voice.desc}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {isActive && <Check className="w-3 h-3 text-emerald-400" />}
          <button
            onClick={e => { e.stopPropagation(); onPreview(voice.id); }}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            disabled={previewing}
            data-testid={`preview-voice-${voice.id}`}
          >
            {previewing === voice.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export function VoiceConfigPanel({ hotelId, agent = 'bitsy', compact = false }) {
  const [config, setConfig] = useState(null);
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [localVoice, setLocalVoice] = useState('');
  const [localSpeed, setLocalSpeed] = useState(1.0);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/hotels/${hotelId}/voice-config`);
      const data = await res.json();
      setConfig(data.config);
      setVoices(data.available_voices || []);
      setLocalVoice(data.config[`${agent}_voice`] || 'shimmer');
      setLocalSpeed(data.config[`${agent}_speed`] || 1.0);
    } catch { /* ignore */ }
  }, [hotelId, agent]);

  useEffect(() => { if (hotelId) fetchConfig(); }, [fetchConfig, hotelId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {};
      body[`${agent}_voice`] = localVoice;
      body[`${agent}_speed`] = localSpeed;
      const res = await fetch(`${API}/api/hotels/${hotelId}/voice-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(`Voice updated to ${localVoice}`);
        fetchConfig();
      }
    } catch {
      toast.error('Failed to save voice config');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (voiceId) => {
    setPreviewing(voiceId);
    try {
      const res = await fetch(`${API}/api/metaverse/bitsy-greeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotel_id: hotelId, room_type: 'Deluxe Room' }),
      });
      const data = await res.json();
      if (data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audio.volume = 0.7;
        audio.onended = () => setPreviewing(null);
        audio.play().catch(() => setPreviewing(null));
      } else {
        setPreviewing(null);
      }
    } catch {
      setPreviewing(null);
    }
  };

  if (!config) return null;

  const agentLabel = agent === 'bitsy' ? 'Bitsy (Concierge)' : agent === 'marina' ? 'Marina (Staff)' : 'Sage (Manager)';
  const hasChanges = localVoice !== (config[`${agent}_voice`] || 'shimmer') || localSpeed !== (config[`${agent}_speed`] || 1.0);

  return (
    <div className={`space-y-3 ${compact ? '' : 'p-3 rounded-lg bg-white/[0.02] border border-white/5'}`} data-testid={`voice-config-${agent}`}>
      {!compact && (
        <div className="flex items-center gap-2">
          <Volume2 className="w-3.5 h-3.5 text-[#F7931A]" />
          <p className="text-[10px] uppercase tracking-widest text-gray-500 heading-font">{agentLabel} Voice</p>
        </div>
      )}

      <div className="space-y-1.5">
        {voices.map(v => (
          <VoiceCard
            key={v.id}
            voice={v}
            selected={localVoice}
            onSelect={setLocalVoice}
            onPreview={handlePreview}
            previewing={previewing}
          />
        ))}
      </div>

      {/* Speed */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-gray-500">Speech Speed</p>
          <Badge variant="outline" className="text-[9px] border-white/10 text-gray-400 mono">{localSpeed.toFixed(2)}x</Badge>
        </div>
        <Slider
          value={[localSpeed]}
          min={0.5}
          max={1.5}
          step={0.05}
          onValueChange={v => setLocalSpeed(v[0])}
          className="py-1"
          data-testid={`speed-slider-${agent}`}
        />
        <div className="flex justify-between text-[8px] text-gray-600">
          <span>Slow</span>
          <span>Normal</span>
          <span>Fast</span>
        </div>
      </div>

      {/* Save */}
      {hasChanges && (
        <Button
          size="sm"
          className="w-full text-xs h-8 bg-[#F7931A] hover:bg-[#F7931A]/90 text-black"
          onClick={handleSave}
          disabled={saving}
          data-testid={`save-voice-${agent}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : null}
          Save Voice
        </Button>
      )}
    </div>
  );
}
